import {
  faEnvelope,
  faImage,
  faMessage
} from '@fortawesome/pro-solid-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import { Fragment, type ReactElement, useEffect, useState } from 'react'
import { getApiError } from '~/api/problem'
import {
  usePathViewedActivity,
  useTrackPathViewedMutation
} from '~/api/queries/pathViewed'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonIcon } from '~/components/Global/Interactions/Button/ButtonIcon'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { EmailAction } from '~/components/Scopes/Actions/EmailAction'
import { MessageAction } from '~/components/Scopes/Actions/MessageAction'
import { useHistory } from '~/domains/actions/queries/useHistory'
import type { ApplicantHistoryEvent } from '~/domains/actions/types'
import { getEventChronoDate } from '~/domains/actions/utils/eventChronoDate'
import { HistoryEventCard } from '~/domains/applicants/components/History/HistoryCards'
import { HistoryProvider } from '~/domains/applicants/components/History/HistoryContext'
import { formatRelativeTime } from '~/utils/date'
import { useReverseInfiniteScroll } from '../../hooks/useReverseInfiniteScroll'
import { isMessageBubbleEvent, type SelectedConversation } from '../../types'
import { DateSeparator } from '../DateSeparator/DateSeparator'
import { MessageBubble } from '../MessageBubble/MessageBubble'
import { MessageBubbleShimmer } from '../MessageBubble/MessageBubbleShimmer'
import {
  threadActions,
  threadActionsButton,
  threadEvent,
  threadImagesNotice,
  threadLoadMore,
  threadMessages,
  threadPanel,
  threadWrapper
} from './MessageThread.css'

// Only request action types that both have a renderer in HistoryEventCard/
// MessageBubble and are recognized by the backend for this entity
// (see App\Enum\ActionType::applicantLinkedFrom/contactLinkedFrom) — otherwise
// a date separator can show up with no event under it.
// Day is the finest unit: "aujourd'hui", "hier", then weeks and months
const separatorLabel = (event: ApplicantHistoryEvent): string =>
  formatRelativeTime(new Date(getEventChronoDate(event)), 3)

const APPLICANT_HISTORY_TYPES = new Set([
  'recall',
  'recall_schedule',
  'meeting_applicant',
  'send_email',
  'email_received',
  'sms_sent',
  'sms_received',
  'status_update',
  'notes',
  'availability_update'
])

const CONTACT_HISTORY_TYPES = new Set([
  'recall',
  'meeting_contact',
  'send_email',
  'email_received',
  'sms_sent',
  'sms_received',
  'create_order'
])

type ActivePanel = 'sms' | 'email' | null

const PANEL_FORM_ID: Record<Exclude<ActivePanel, null>, string> = {
  sms: 'message-thread-sms-form',
  email: 'message-thread-email-form'
}

type MessageThreadProps = {
  conversation: SelectedConversation
}

export const MessageThread = ({
  conversation
}: MessageThreadProps): ReactElement => {
  const queryClient = useQueryClient()
  const trackPathViewed = useTrackPathViewedMutation()
  const [activePanel, setActivePanel] = useState<ActivePanel>(null)
  const [showInlineImages, setShowInlineImages] = useState(false)

  const {
    items,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useHistory({
    entityType: conversation.type,
    entityId: conversation.id,
    selectedTypes:
      conversation.type === 'applicant'
        ? APPLICANT_HISTORY_TYPES
        : CONTACT_HISTORY_TYPES,
    includeContent: true
  })
  const isForbidden = getApiError(error)?.status === 403

  const path = `/messaging/${conversation.type}/${conversation.id}`
  const { containerRef, topSentinelRef, bottomRef } = useReverseInfiniteScroll({
    items,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    resetKey: path
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: trackPathViewed.mutate is stable
  useEffect(() => {
    trackPathViewed.mutate({ path })
    setActivePanel(null)
    setShowInlineImages(false)
  }, [path])

  // New activity on the conversation reloads the thread; while it is open and visible, the
  // message is read on the spot.
  usePathViewedActivity(path, {
    onNewActivity: () => {
      void queryClient.invalidateQueries({
        queryKey: [conversation.type, conversation.id, 'history']
      })
      if (document.visibilityState === 'visible') {
        trackPathViewed.mutate({ path })
      }
    }
  })

  const reversedItems = items ? [...items].reverse() : []
  const hasHiddenInlineImages =
    !showInlineImages &&
    reversedItems.some(
      (event) =>
        isMessageBubbleEvent(event) &&
        'emailInlineImages' in event &&
        (event.emailInlineImages ?? []).length > 0
    )

  const openPanel = (panel: Exclude<ActivePanel, null>) => {
    if (activePanel === panel) {
      const form = document.getElementById(PANEL_FORM_ID[panel])
      if (form instanceof HTMLFormElement) {
        form.requestSubmit()
      }
      return
    }
    setActivePanel(panel)
  }

  return (
    <div className={threadWrapper}>
      {hasHiddenInlineImages && (
        <div className={threadImagesNotice}>
          Images masquées dans cette conversation
          <Button
            buttonSize="small"
            variation={ButtonVariation.Secondary}
            onClick={() => setShowInlineImages(true)}
          >
            <ButtonIcon icon={faImage} />
            Afficher les images
          </Button>
        </div>
      )}

      <HistoryProvider embedded>
        <div className={threadMessages} ref={containerRef}>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: shimmer placeholder items have no natural key
              <MessageBubbleShimmer key={i} inbound={i % 2 === 0} />
            ))}

          {isError && (
            <NoResult
              message={
                isForbidden
                  ? "Vous n'avez pas accès à cette conversation"
                  : 'Impossible de charger cette conversation'
              }
              isError
              isSmall
            />
          )}

          {!isLoading && !isError && reversedItems.length === 0 && (
            <NoResult message="Aucun message" isSmall />
          )}

          {hasNextPage && (
            <div ref={topSentinelRef} className={threadLoadMore}>
              {isFetchingNextPage && <Loader small />}
            </div>
          )}

          {reversedItems.map((event, index) => {
            // Events are grouped under the label the separator shows, so two dates that read
            // the same ("il y a 3 semaines") never get two separators in a row
            const dateLabel = separatorLabel(event)
            const prevEvent = index > 0 ? reversedItems[index - 1] : undefined
            const showSeparator =
              !prevEvent || separatorLabel(prevEvent) !== dateLabel

            return (
              <Fragment key={event.id}>
                {showSeparator && <DateSeparator label={dateLabel} />}
                {isMessageBubbleEvent(event) ? (
                  <MessageBubble
                    event={event}
                    showInlineImages={showInlineImages}
                  />
                ) : (
                  <div className={threadEvent}>
                    <HistoryEventCard event={event} />
                  </div>
                )}
              </Fragment>
            )
          })}

          <div ref={bottomRef} />
        </div>
      </HistoryProvider>

      {activePanel === 'sms' && (
        <div className={threadPanel}>
          <MessageAction
            applicantIds={
              conversation.type === 'applicant' ? [conversation.id] : undefined
            }
            contactIds={
              conversation.type === 'contact' ? [conversation.id] : undefined
            }
            onClose={() => setActivePanel(null)}
            formId={PANEL_FORM_ID.sms}
          />
        </div>
      )}

      {activePanel === 'email' && (
        <div className={threadPanel}>
          <EmailAction
            applicantIds={
              conversation.type === 'applicant' ? [conversation.id] : undefined
            }
            contactsIds={
              conversation.type === 'contact' ? [conversation.id] : undefined
            }
            sendTo={conversation.type === 'contact' ? 'contact' : 'applicant'}
            onClose={() => setActivePanel(null)}
            formId={PANEL_FORM_ID.email}
          />
        </div>
      )}

      <div className={threadActions}>
        <ButtonGroup>
          <Button
            className={threadActionsButton}
            buttonSize="small"
            variation={
              activePanel === 'sms'
                ? ButtonVariation.Primary
                : ButtonVariation.Secondary
            }
            onClick={() => openPanel('sms')}
          >
            <ButtonIcon icon={faMessage} />
            Répondre par SMS
          </Button>
          <Button
            className={threadActionsButton}
            buttonSize="small"
            variation={
              activePanel === 'email'
                ? ButtonVariation.Primary
                : ButtonVariation.Secondary
            }
            onClick={() => openPanel('email')}
          >
            <ButtonIcon icon={faEnvelope} />
            Répondre par email
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}
